'use client';
import { Disclosure, Transition } from '@headlessui/react';
import Arrow from 'components/Arrow';
import Typography from 'components/Typography';
import {
  forwardRef,
  ForwardRefRenderFunction,
  MouseEvent,
  PropsWithChildren,
  ReactNode
} from 'react';
import { cn } from 'utils/cn';

interface IAccordion {
  title?: ReactNode;
  defaultOpen?: boolean;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}

const Accordion: ForwardRefRenderFunction<HTMLButtonElement, PropsWithChildren<IAccordion>> = (
  { children, title, onClick, defaultOpen },
  ref
) => {
  return (
    <div className=" my-4 w-full">
      <div className="w-full">
        <Disclosure defaultOpen={defaultOpen}>
          {({ open }) => (
            <div
              className={cn(
                'rounded-6xl  transition-colors duration-200 ease-in-out',
                'border-1 border-tolq-gray-100 bg-white',
                open && ' shadow-custom-light-gray outline-none'
              )}
            >
              <Disclosure.Button as="div">
                <button
                  onClick={onClick}
                  className={cn(
                    'transition-colors duration-200 ease-in-out',
                    'group flex w-full items-center justify-between',
                    'text-left text-sm font-medium',
                    'focus-visible:ring-offset-primary-blue-500/75',
                    'focus:outline-none focus-visible:ring',
                    'px-4 py-2',
                    'text-tertiary-100 hover:bg-tolq-gray-200',
                    'rounded-6xl  bg-white',
                    open && 'rounded-b-none '
                  )}
                  data-value={open}
                  ref={ref}
                >
                  {typeof title === 'string' ? (
                    <Typography variant="regular3">{title}</Typography>
                  ) : (
                    <>{title}</>
                  )}
                  <Arrow open={!open} className="size-7 !text-tolq-black-200" />
                </button>
              </Disclosure.Button>
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0 h-0"
                enterTo="transform scale-100 opacity-100 h-full"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100 h-full"
                leaveTo="transform scale-95 opacity-0 h-0"
              >
                <Disclosure.Panel className="px-6 py-4 text-sm">{children}</Disclosure.Panel>
              </Transition>
            </div>
          )}
        </Disclosure>
      </div>
    </div>
  );
};

export default forwardRef(Accordion);
